import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HeartHandshake, AlertTriangle, CheckCircle2, Plus, ExternalLink, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SdohReferralDialog } from "./SdohReferralDialog";
import { format } from "date-fns";

interface PatientSdohSummaryProps {
  sdohScreenings: string[] | null;
  sdohNotes?: string | null;
  memberId?: string;
}

// Risk answer mappings — answers that indicate a social need
const RISK_ANSWERS: Record<string, string[]> = {
  es_food_insecurity: ["Sometimes true", "Often true"],
  es_utility_difficulty: ["Yes", "Already shut off"],
  es_employment: ["Unemployed and looking for work", "Unemployed and not looking for work"],
  eaq_education_level: ["Less than high school"],
  eaq_health_literacy: ["Not confident at all", "A little confident"],
  hcaq_insurance: ["No"],
  hcaq_transportation: ["Yes"],
  hcaq_pcp: ["No"],
  nbe_housing: ["I have a place to live, but I am worried about losing it", "I do not have a steady place to live"],
  nbe_housing_problems: ["Pests (bugs, mice)", "Mold", "Lack of heat", "Stove or oven not working", "Water leaks"],
  nbe_safety: ["A little unsafe", "Very unsafe"],
  scc_social_support: ["Rarely", "Never"],
  scc_safety_threat: ["Sometimes", "Often"],
  scc_wants_help: ["Yes"],
};

const QUESTION_LABELS: Record<string, string> = {
  es_food_insecurity: "Food insecurity",
  es_utility_difficulty: "Utility hardship",
  es_employment: "Employment",
  eaq_education_level: "Education",
  eaq_health_literacy: "Health literacy",
  hcaq_insurance: "No insurance",
  hcaq_transportation: "Transportation barriers",
  hcaq_pcp: "No primary care provider",
  nbe_housing: "Housing instability",
  nbe_housing_problems: "Housing conditions",
  nbe_safety: "Neighborhood safety",
  scc_social_support: "Social isolation",
  scc_safety_threat: "Safety concern",
  scc_wants_help: "Wants help",
};

const DOMAIN_MAP: Record<string, string> = {
  es_food_insecurity: "Economic Stability",
  es_utility_difficulty: "Economic Stability",
  es_employment: "Economic Stability",
  eaq_education_level: "Education Access",
  eaq_health_literacy: "Education Access",
  hcaq_insurance: "Healthcare Access",
  hcaq_transportation: "Healthcare Access",
  hcaq_pcp: "Healthcare Access",
  nbe_housing: "Neighborhood",
  nbe_housing_problems: "Neighborhood",
  nbe_safety: "Neighborhood",
  scc_social_support: "Social Context",
  scc_safety_threat: "Social Context",
  scc_wants_help: "Social Context",
};

const STATUS_COLORS: Record<string, string> = {
  referred: "border-blue-300 bg-blue-50 text-blue-800 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-700",
  in_progress: "border-yellow-300 bg-yellow-50 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-700",
  resolved: "border-green-300 bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-300 dark:border-green-700",
};

const STATUS_LABELS: Record<string, string> = {
  referred: "Referred",
  in_progress: "In Progress",
  resolved: "Resolved",
};

function parseScreenings(screenings: string[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const entry of screenings) {
    const idx = entry.indexOf("::");
    if (idx > -1) {
      result[entry.substring(0, idx)] = entry.substring(idx + 2);
    }
  }
  return result;
}

export function PatientSdohSummary({ sdohScreenings, sdohNotes, memberId }: PatientSdohSummaryProps) {
  const queryClient = useQueryClient();
  const [referralDialog, setReferralDialog] = useState<{
    open: boolean;
    needLabel: string;
    domain: string;
  }>({ open: false, needLabel: "", domain: "" });

  // Fetch existing referrals for this member
  const { data: referrals } = useQuery({
    queryKey: ["sdoh-referrals", memberId],
    queryFn: async () => {
      if (!memberId) return [];
      const { data, error } = await supabase
        .from("sdoh_referrals" as any)
        .select("*")
        .eq("member_id", memberId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!memberId,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updates: any = { status };
      if (status === "in_progress") updates.followed_up_at = new Date().toISOString();
      if (status === "resolved") updates.resolved_at = new Date().toISOString();
      const { error } = await supabase
        .from("sdoh_referrals" as any)
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      toast.success(`Referral marked as ${STATUS_LABELS[status] || status}`);
      queryClient.invalidateQueries({ queryKey: ["sdoh-referrals", memberId] });
    },
    onError: (err: any) => toast.error("Failed to update status", { description: err.message }),
  });

  if (!sdohScreenings || sdohScreenings.length === 0) {
    return null;
  }

  const parsed = parseScreenings(sdohScreenings);
  const flagged: { id: string; label: string; answer: string; domain: string }[] = [];
  const safe: string[] = [];

  for (const [questionId, answer] of Object.entries(parsed)) {
    const riskValues = RISK_ANSWERS[questionId];
    if (!riskValues) continue;

    const answers = answer.split("||");
    const isRisk = answers.some((a) => riskValues.includes(a));

    if (isRisk) {
      flagged.push({
        id: questionId,
        label: QUESTION_LABELS[questionId] || questionId,
        answer: answers.filter((a) => riskValues.includes(a)).join(", "),
        domain: DOMAIN_MAP[questionId] || "Other",
      });
    } else {
      const label = QUESTION_LABELS[questionId];
      if (label) safe.push(label);
    }
  }

  // Group flagged items by domain
  const byDomain: Record<string, typeof flagged> = {};
  for (const item of flagged) {
    if (!byDomain[item.domain]) byDomain[item.domain] = [];
    byDomain[item.domain].push(item);
  }

  // Map referrals by need_label for quick lookup
  const referralsByNeed: Record<string, any[]> = {};
  for (const r of referrals || []) {
    if (!referralsByNeed[r.need_label]) referralsByNeed[r.need_label] = [];
    referralsByNeed[r.need_label].push(r);
  }

  const hasCritical = flagged.some((f) =>
    ["nbe_housing", "es_food_insecurity", "scc_safety_threat", "nbe_safety"].includes(f.id)
  );

  return (
    <>
      <Card className={hasCritical ? "border-orange-300 bg-orange-50/50 dark:bg-orange-950/10 dark:border-orange-800" : ""}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <HeartHandshake className="h-5 w-5 text-primary" />
            SDOH Screening Summary
            {flagged.length > 0 ? (
              <Badge variant="destructive" className="ml-auto text-xs">
                {flagged.length} Need{flagged.length !== 1 ? "s" : ""} Identified
              </Badge>
            ) : (
              <Badge variant="outline" className="ml-auto text-xs text-green-700 border-green-300 bg-green-50">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                No Risks Flagged
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {flagged.length > 0 && (
            <TooltipProvider>
              <div className="space-y-3">
                {Object.entries(byDomain).map(([domain, items]) => (
                  <div key={domain}>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                      {domain}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((item) => {
                        const itemReferrals = referralsByNeed[item.label] || [];
                        const hasReferral = itemReferrals.length > 0;
                        const latestStatus = hasReferral ? itemReferrals[0].status : null;

                        return (
                          <div key={item.id} className="flex items-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className={
                                    hasReferral
                                      ? `cursor-default ${STATUS_COLORS[latestStatus] || STATUS_COLORS.referred}`
                                      : "cursor-default border-orange-300 bg-orange-50 text-orange-800 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-700"
                                  }
                                >
                                  {hasReferral ? (
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                  ) : (
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                  )}
                                  {item.label}
                                  {hasReferral && (
                                    <span className="ml-1 text-[10px] opacity-75">
                                      ({STATUS_LABELS[latestStatus] || latestStatus})
                                    </span>
                                  )}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs">
                                <p className="text-xs">
                                  <span className="font-medium">Response:</span> {item.answer}
                                </p>
                                {hasReferral && (
                                  <p className="text-xs mt-1">
                                    <span className="font-medium">Referred to:</span>{" "}
                                    {itemReferrals[0].resource_name}
                                    {itemReferrals[0].referred_at && (
                                      <span className="opacity-75">
                                        {" "}— {format(new Date(itemReferrals[0].referred_at), "MMM d, yyyy")}
                                      </span>
                                    )}
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                            {memberId && !hasReferral && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 rounded-full"
                                    onClick={() =>
                                      setReferralDialog({
                                        open: true,
                                        needLabel: item.label,
                                        domain: item.domain,
                                      })
                                    }
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Log referral</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </TooltipProvider>
          )}

          {/* Referral summary */}
          {referrals && referrals.length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Referrals ({referrals.length})
              </p>
              <div className="space-y-1.5">
                {referrals.slice(0, 4).map((r: any) => (
                  <div key={r.id} className="flex items-center justify-between text-xs rounded-md border px-2.5 py-1.5 bg-muted/30">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{r.need_label}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="truncate text-muted-foreground">{r.resource_name}</span>
                    </div>
                    <Select
                      value={r.status}
                      onValueChange={(value) => updateStatus.mutate({ id: r.id, status: value })}
                    >
                      <SelectTrigger className={`h-5 w-auto gap-1 text-[10px] px-1.5 py-0 border rounded-full shrink-0 ${STATUS_COLORS[r.status] || ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="referred">Referred</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sdohNotes && (
            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground mb-1">Patient Notes</p>
              <p className="text-sm text-foreground">{sdohNotes}</p>
            </div>
          )}

          {flagged.length === 0 && safe.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {safe.length} area{safe.length !== 1 ? "s" : ""} screened with no concerns identified.
            </p>
          )}
        </CardContent>
      </Card>

      {memberId && (
        <SdohReferralDialog
          open={referralDialog.open}
          onOpenChange={(open) => setReferralDialog((prev) => ({ ...prev, open }))}
          memberId={memberId}
          needLabel={referralDialog.needLabel}
          domain={referralDialog.domain}
        />
      )}
    </>
  );
}
